import { logger } from "@/lib/logger";
import { useEffect, useState } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Loader2 } from 'lucide-react';
import NotFound from '@/pages/NotFound';

/**
 * VanityInviteRedirect Component
 * 
 * Handles vanity URLs for trial site registration links.
 * Checks if a slug matches a valid trial site invite_slug and redirects accordingly.
 * 
 * Example: /impact-research-kisumu-pilot
 * Redirects to: /register/site/impact-research-kisumu-pilot
 */
export default function VanityInviteRedirect() {
  const params = useParams();
  const siteSlug = params.siteSlug;
  const [checking, setChecking] = useState(true);
  const [isValid, setIsValid] = useState(false);

  useEffect(() => {
    const checkSlug = async () => {
      if (!siteSlug) {
        setChecking(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .rpc('get_trial_site_by_invite_slug', { p_invite_slug: siteSlug });

        if (error) {
          logger.error('Error checking invite slug:', error);
          setIsValid(false);
        } else if (data && data.length > 0) {
          setIsValid(true);
        } else {
          setIsValid(false);
        }
      } catch (err) {
        logger.error('Error validating vanity URL:', err);
        setIsValid(false);
      } finally {
        setChecking(false);
      }
    };

    checkSlug();
  }, [siteSlug]);

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (isValid && siteSlug) {
    return <Navigate to={`/register/site/${siteSlug}`} replace />;
  }

  return <NotFound />;
}